import React, { Component } from 'react';
import { reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { PopoverPosition } from '@blueprintjs/core';
import { debounce, isEmpty, isEqual, sortBy } from 'lodash';
import { MAX_TRACE_ITEMS } from '@kentik/ui-shared/synthetics/constants';
import { Box, Card, EmptyState, Flex, Text, Icon, Button, Popover, Tag, Tooltip } from 'core/components';
import { Field, Form, InputGroup, Select, Switch } from 'core/form';
import MultiSlider, { Handle } from 'core/components/MultiSlider';
import { safelyParseJSON } from 'core/util';
import { showInfoToast } from 'core/components/toast';
import SynthFilterBar from 'app/views/synthetics/components/testResults/filters/SynthFilterBar';
import { TAB_FILTERS } from 'app/views/synthetics/components/testResults/filters/tabFilterConfig';
import {
  clampTraceSelection,
  ipsToSharedTargets,
  sharedTargetsToIps,
  TraceSelectionScope
} from 'app/views/synthetics/components/traceTargetMapping';
import { FiInfo } from 'react-icons/fi';
import ToggleButtonGroup from 'core/components/ToggleButtonGroup';
import Traceroute from './Traceroute';
import TracerouteTable from './TracerouteTable';

const fields = {
  selectedAgents: {
    label: 'Show',
    defaultValue: []
  },
  selectedIps: {
    defaultValue: []
  },
  selectedAgent: {
    label: 'Agent',
    placeholder: 'Select an agent',
    defaultValue: ''
  },
  selectedIp: {
    label: 'Target',
    placeholder: 'Select a target',
    defaultValue: ''
  },
  groupBy: {
    label: 'Group hops by',
    options: [
      { label: 'No Grouping', value: 'none' },
      { label: 'ASN', value: 'asn' },
      { label: 'Site', value: 'site' }
    ],
    defaultValue: 'none'
  },
  percSuccessThreshold: {
    defaultValue: 0.9,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' },
    transform: {
      in: (value) => {
        if (value === '') {
          return 0;
        }
        return value * 100;
      },
      out: (value) => {
        if (value === '') {
          return 0;
        }
        return value / 100;
      }
    }
  },
  deltaLatencyThreshold: {
    defaultValue: 50,
    rules: 'numeric',
    messages: { numeric: 'Not a valid number' }
  },
  moreThanExpected: {
    label: 'Highlight links exceeding geo latency estimate',
    defaultValue: true
  },
  collapseFromStart: {
    label: 'After Agents',
    defaultValue: 0
  },
  collapseFromEnd: {
    label: 'Before Target',
    defaultValue: 0
  },
  openPaths: {
    defaultValue: null
  },
  collapseTimeouts: {
    label: 'Collapse Timeouts',
    defaultValue: true
  },
  viewMode: {
    defaultValue: 'graph'
  }
};

@inject('$app', '$exports', '$auth', '$dataviews', '$syn')
@Form({ fields, options: { name: 'Traceroute Options' } })
@observer
class TestPath extends Component {
  static defaultProps = {
    allowExport: true,
    isPreview: false
  };

  constructor(props) {
    super(props);

    const { $exports, form, allowExport, testResults } = this.props;

    if (allowExport) {
      $exports.getSettings().then(({ hashedTracerouteOpts }) => {
        let savedTracerouteOpts = {};

        if (hashedTracerouteOpts) {
          savedTracerouteOpts = hashedTracerouteOpts || {};
        } else if (window.localStorage) {
          savedTracerouteOpts = safelyParseJSON(localStorage.getItem('tracerouteOpts')) || {};
        }

        // Don't restore agent/target selections from saved state — they may be stale
        // and point to agents not in the current trace data. Only restore display preferences.
        const { selectedAgents, selectedIps, selectedAgent, selectedIp, ...displayOpts } = savedTracerouteOpts;
        form.setValues({ ...displayOpts });
      });
    }

    this._savedGraphAgents = null; // multi-select agents to restore on graph switch
    this._savedGraphIps = null; // multi-select targets to restore on graph switch
    this.selectionScope = new TraceSelectionScope();

    // initialize selected agent ids and target ips
    // the target ips are a list of objects that provide an ip and optional agent id when available
    const initAgentIds = testResults.lastFetchedTraceParams.agentIds;
    const initTargetIps = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || [];

    // Synchronously read saved viewMode from localStorage to prevent flash on remount
    const savedTracerouteOptsSync = safelyParseJSON(localStorage.getItem('tracerouteOpts')) || {};
    const savedViewMode = savedTracerouteOptsSync.viewMode || 'graph';
    const savedSelectedIp = savedTracerouteOptsSync.selectedIp || '';
    const savedSelectedAgent = savedTracerouteOptsSync.selectedAgent || '';

    // In table mode, restore the saved single-select agent/target if they exist in the current data
    const useTableSelections = savedViewMode === 'table';

    let resolvedAgent = initAgentIds.length ? String(initAgentIds[0]) : '';
    if (useTableSelections && savedSelectedAgent && initAgentIds.includes(savedSelectedAgent)) {
      resolvedAgent = savedSelectedAgent;
    }

    let resolvedIp = initTargetIps.length ? initTargetIps[0] : '';
    if (useTableSelections && savedSelectedIp && initTargetIps.includes(savedSelectedIp)) {
      resolvedIp = savedSelectedIp;
    }

    form.setValues({
      viewMode: savedViewMode,
      selectedAgents: useTableSelections && resolvedAgent ? [resolvedAgent] : initAgentIds,
      selectedIps: useTableSelections && resolvedIp ? [resolvedIp] : initTargetIps,
      selectedAgent: resolvedAgent,
      selectedIp: resolvedIp
    });

    form.onChange = this.handleFormChange;
  }

  componentDidMount() {
    const { testResults } = this.props;

    // Debounce the traceroute refetch so rapid multi-select toggles fire a single query.
    this.debouncedTraceRefetch = debounce((opts) => this.handleAgentTargetClose(opts), 1000);

    // Keep this panel's form in step with the shared filter bar, which writes straight to
    // testResults.filters. handleViewModeChange also writes back, so the reaction can be woken by
    // its own side of the sync — bail when the form already matches rather than refetching traces.
    this.sharedFilterDisposer = reaction(
      () => ({
        agents: testResults.sharedFilterAgentIds.slice(),
        targets: testResults.sharedFilterTargets.slice()
      }),
      ({ agents, targets }, prev) => {
        // handleViewModeChange writes form state into the shared filters; the form is already correct
        if (this._writingSharedFilters) {
          return;
        }

        const { form } = this.props;
        const current = form.getValues();
        const isTable = current.viewMode === 'table';
        const maxItems = isTable ? 1 : MAX_TRACE_ITEMS;
        const ignoreTargets = this.isHostnameTargetedTest;
        const { allTargets } = testResults.lastFetchedTraceParams;
        const updates = {};

        this.selectionScope.observe({ agents, targets }, { isTable });

        // Shrink wider selections (e.g. from Metrics) to what traceroute shows; table keeps the newest pick.
        // The narrowing is scoped to this tab: the wider selection is handed back on unmount.
        const { selection: clamped, truncated } = clampTraceSelection({ agents, targets }, maxItems, isTable);
        const { agents: agentIds, targets: sharedTargets } = clamped;
        if (truncated) {
          this.selectionScope.saveBeforeNarrowing({ agents, targets });
          this.writeSharedFilters(agentIds, sharedTargets);
          showInfoToast(
            isTable
              ? 'Traceroute table view shows one agent and target, so the filter selection is narrowed on this tab.'
              : `Traceroute shows up to ${MAX_TRACE_ITEMS} agents and targets, so the filter selection is narrowed on this tab.`
          );
        }
        const targetIds = ignoreTargets ? [] : sharedTargets;

        // Empty shared filter means "no filter": fall back to the page-load trace selection
        const { agentIds: defaultAgents, targetIps: defaultIps } = testResults.defaultTraceSelection;
        const agentsCleared = agentIds.length === 0 && prev?.agents.length > 0;
        const targetsCleared = !ignoreTargets && targetIds.length === 0 && prev?.targets.length > 0;
        const targetIps = sharedTargetsToIps(testResults.test, targetIds, allTargets);
        const formTargets = ipsToSharedTargets(testResults.test, current.selectedIps, allTargets);

        let nextAgents = agentIds.map(String);
        if (agentsCleared) {
          nextAgents = defaultAgents;
        }
        let nextIps = targetsCleared ? defaultIps : targetIps;
        nextAgents = nextAgents.slice(0, maxItems);
        nextIps = nextIps.slice(0, maxItems);

        if ((agentIds.length > 0 || agentsCleared) && !isEqual(nextAgents, current.selectedAgents)) {
          updates.selectedAgents = nextAgents;
        }
        if (
          targetsCleared
            ? !isEqual(nextIps, current.selectedIps)
            : targetIps.length > 0 && !isEqual(sortBy(targetIds.map(String)), sortBy(formTargets))
        ) {
          updates.selectedIps = nextIps;
        }

        if (Object.keys(updates).length > 0) {
          form.setValues(updates);
          this.debouncedTraceRefetch({ allowEmptyTargets: targetsCleared });
        }
      },
      { fireImmediately: true }
    );
  }

  componentWillUnmount() {
    this.sharedFilterDisposer?.();
    this.debouncedTraceRefetch?.cancel();

    // Hand the pre-narrowing selection back to the other tabs (reaction is disposed, so this won't loop)
    const restore = this.selectionScope.takeRestore(this.sharedSelection);
    if (restore) {
      this.writeSharedFilters(restore.agents, restore.targets);
    }
  }

  get sharedSelection() {
    const { testResults } = this.props;
    return { agents: testResults.sharedFilterAgentIds.slice(), targets: testResults.sharedFilterTargets.slice() };
  }

  componentDidUpdate(prevProps) {
    const { form, traceroutes, testResults } = this.props;

    if (traceroutes !== prevProps.traceroutes) {
      const { agentIds } = testResults.lastFetchedTraceParams;
      const { viewMode } = form.getValues();

      if (viewMode === 'table') {
        // In table mode, only set agent/target if they're empty (initial load).
        // Preserve the user's current target selection when changing agents —
        // TracerouteTable handles the case where the selected target has no data.
        const { selectedIps, selectedAgents } = form.getValues();
        const updates = {};

        if (!selectedAgents.length && agentIds.length) {
          updates.selectedAgents = [String(agentIds[0])];
          updates.selectedAgent = String(agentIds[0]);
        }

        if (!selectedIps.length && traceroutes.length) {
          const firstTarget = this.getFirstAvailableTarget(traceroutes);
          if (firstTarget) {
            updates.selectedIps = [firstTarget];
            updates.selectedIp = firstTarget;
          }
        }

        if (Object.keys(updates).length) {
          form.setValues(updates);
        } else {
          // No updates needed — preserve current selection
        }
      } else {
        const updates = { selectedAgents: agentIds };
        // A cleared target filter refetches with no target ips; adopt whatever the server selected
        if (!form.getValues().selectedIps?.length) {
          updates.selectedIps = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip);
        }
        form.setValues(updates);
      }
    }
  }

  // Get the first available target IP from trace data
  getFirstAvailableTarget = (traceroutes) => {
    if (!traceroutes?.length) {
      return null;
    }
    const traceroute = traceroutes[0];
    if (!traceroute?.traces?.length) {
      return null;
    }
    return traceroute.traces[0]?.target?.target_ip || null;
  };

  handleAgentTargetClose = ({ allowEmptyTargets = false } = {}) => {
    const { form, testResults } = this.props;
    const { selectedAgents, selectedIps } = form.getValues();
    const lastFetchedAgentIds = testResults.lastFetchedTraceParams.agentIds;
    const lastFetchedTargetIps = testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip);
    const agentsChanged = !isEqual(selectedAgents, lastFetchedAgentIds);
    const targetsChanged = !this.isHostnameTargetedTest && !isEqual(selectedIps, lastFetchedTargetIps);
    const hasChangedData = agentsChanged || targetsChanged;

    if (
      hasChangedData &&
      selectedAgents.length &&
      (selectedIps.length || !this.targetOptions.length || allowEmptyTargets || this.isHostnameTargetedTest)
    ) {
      // if selections have changed in either agents or targets AND we have something to fetch for
      // the logic that checks for blank target options covers a scenario where a test that relies on targets returned from the trace query that contains only offline agents can still re-fetch
      // run the trace results query again using the current date range and the currently selected agents and ips

      // Wipe cache since the user explicitly changed filter params
      testResults.traceResultsTs = {};
      testResults.lastFetchedTraceParams = { ...testResults.lastFetchedTraceParams, agentIds: [], targetIps: [] };
      testResults.fetchTraceResults({
        start_time: testResults.actualStartDate,
        end_time: testResults.actualEndDate,
        agent_ids: selectedAgents,
        // resolved IPs vary (incl. v4/v6) for hostname-targeted tests, so never pin them
        target_ips: this.isHostnameTargetedTest ? [] : selectedIps
      });
    }
  };

  handleViewModeChange = (newMode) => {
    const { form, testResults } = this.props;
    const { selectedAgents, selectedIps } = form.getValues();
    const { allTargets } = testResults.lastFetchedTraceParams;

    if (newMode === 'table') {
      // Save current multi-select state for later restoration
      this._savedGraphAgents = selectedAgents;
      this._savedGraphIps = selectedIps;
      this.selectionScope.saveBeforeNarrowing(this.sharedSelection);

      const firstAgent = selectedAgents.length ? selectedAgents[0] : '';
      const firstIp = selectedIps.length ? selectedIps[0] : '';

      // Auto-select first agent and first target for table mode
      form.setValues({
        viewMode: 'table',
        selectedAgent: firstAgent,
        selectedIp: firstIp,
        selectedAgents: firstAgent ? [firstAgent] : [],
        selectedIps: firstIp ? [firstIp] : []
      });

      // Table view is a single src/target combo — scope shared filters to it so all tabs agree.
      this.writeSharedFilters(
        firstAgent ? [firstAgent] : [],
        firstIp && !this.isHostnameTargetedTest ? ipsToSharedTargets(testResults.test, [firstIp], allTargets) : []
      );
    } else {
      // Restore saved multi-select state
      const restoredAgents = this._savedGraphAgents || selectedAgents;
      const restoredIps = this._savedGraphIps || selectedIps;

      form.setValues({
        viewMode: 'graph',
        selectedAgents: restoredAgents,
        selectedIps: restoredIps
      });

      // Restore the shared filters from before table view (empty stays "all"), within graph limits
      const { saved } = this.selectionScope;
      if (saved) {
        const { selection } = clampTraceSelection(saved, MAX_TRACE_ITEMS);
        this.writeSharedFilters(selection.agents, selection.targets);
      }

      this._savedGraphAgents = null;
      this._savedGraphIps = null;
    }

    this.saveFormChange();
  };

  writeSharedFilters = (agentIds, targets) => {
    const { testResults } = this.props;

    this.selectionScope.recordWrite({ agents: agentIds, targets });
    this._writingSharedFilters = true;
    try {
      runInAction(() => {
        testResults.setSharedFilterAgentIds(agentIds);
        testResults.setSharedFilterTargets(targets);
      });
    } finally {
      this._writingSharedFilters = false;
    }
  };

  handleFormChange = () => this.saveFormChange();

  saveFormChange = () => {
    const { $exports, form, allowExport } = this.props;
    const values = form.getValues();

    if (allowExport) {
      if (window.localStorage) {
        localStorage.setItem('tracerouteOpts', JSON.stringify(values));
      }
      $exports.setHash({
        hashedTracerouteOpts: values
      });
    }
  };

  onGroupByChange = ({ value: groupBy }) => {
    const { form } = this.props;

    form.setValues({
      groupBy,
      collapseFromStart: 0,
      collapseFromEnd: 0
    });

    this.saveFormChange();
  };

  onOpenPaths = (agentID, target_ip) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? [...openPaths, { agentID, target_ip }] : [{ agentID, target_ip }]
    });

    this.saveFormChange();
  };

  closeOpenPaths = (agentID) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => path.agentID !== agentID) : null
    });

    this.saveFormChange();
  };

  setPaths = (agentIDs) => {
    const { form } = this.props;
    const { openPaths } = form.getValues();

    form.setValues({
      openPaths: openPaths ? openPaths.filter((path) => agentIDs.find((agentID) => path.agentID === agentID)) : null
    });

    this.saveFormChange();
  };

  // HTTP, page load and hostname tests target a hostname/URL — the resolved IP varies per timestamp.
  // For these tests, don't filter traces by target IP.
  get isHostnameTargetedTest() {
    const { testResults } = this.props;
    const { test } = testResults;
    return !!(test && (test.isHttp || test.isPageLoad || test.isHostname));
  }

  get isMeshTest() {
    const { testResults } = this.props;
    const { test } = testResults;
    return !!(test && test.isMesh);
  }

  /*
    Returns a list of select options that hydrate the target selector
    Select options support the following:
      - option values will always be an ip, but will support shipping agent options where available so we can render agents in the combo
  */
  get targetOptions() {
    const { $syn, testResults } = this.props;
    // allTargets contains the comprehensive list of ips returned from the last trace results fetch
    const { allTargets = [] } = testResults.lastFetchedTraceParams || {};

    return allTargets
      .map(({ target_ip, target_agent_id }) => {
        const agentOptions = $syn.agents.getOptionsForAgentsByIds([target_agent_id])[0]; // try to get agent options

        // For mesh tests, show agent name as the label instead of raw IP
        const label = this.isMeshTest && agentOptions?.label ? agentOptions.label : target_ip;

        return {
          label,
          value: target_ip,
          filterLabel: `${agentOptions?.label || ''}-${target_ip}`, // support searching by ip and agent label where available
          agentOptions
        };
      })
      .sort((a, b) => a.filterLabel.localeCompare(b.filterLabel));
  }

  resetForm = () => {
    const { form, testResults } = this.props;

    form.reset();
    form.setValues({
      selectedAgents: testResults.lastFetchedTraceParams.agentIds,
      selectedIps: testResults.lastFetchedTraceParams.selectedTargets.map(({ target_ip }) => target_ip) || []
    });

    this.saveFormChange();
  };

  render() {
    const { test, testResults, traceroutes, useLocalIp, form, onNodeSelect, selectedNode, isPreview, $exports } =
      this.props;
    const { traceResults } = testResults;
    const { max_asn_path_count, max_site_path_count } = traceResults;

    const {
      deltaLatencyThreshold,
      collapseFromStart,
      collapseFromEnd,
      groupBy,
      openPaths,
      percSuccessThreshold,
      selectedAgents,
      selectedIps,
      collapseTimeouts,
      moreThanExpected,
      viewMode
    } = form.getValues();

    const { label: groupByLabel } = fields.groupBy.options.find((o) => o.value === groupBy);
    const groupBtnText =
      groupBy === 'none'
        ? 'None or ASN...' // 'ASN, Site...'
        : `${groupByLabel}: ${collapseFromStart} after agent, ${collapseFromEnd} before target`;
    const maxPathCount = (groupBy === 'asn' && max_asn_path_count) || (groupBy === 'site' && max_site_path_count) || 0;
    const hasResults = !isEmpty(traceResults);

    return (
      <Box>
        <Flex justifyContent="space-between" p={1} mb={2} alignItems="flex-end" gap={2}>
          <Flex alignItems="flex-end" gap={3}>
            <ToggleButtonGroup selectedValue={viewMode}>
              <Button icon="graph" value="graph" onClick={() => this.handleViewModeChange('graph')}>
                Path
              </Button>
              <Button icon="th" value="table" onClick={() => this.handleViewModeChange('table')}>
                Table
              </Button>
            </ToggleButtonGroup>
            <SynthFilterBar
              test={test}
              filters={TAB_FILTERS.path}
              agentSelectMode={viewMode === 'table' ? 'single' : 'multi'}
              agentMaxSelected={MAX_TRACE_ITEMS}
              targetMaxSelected={viewMode === 'table' ? 1 : MAX_TRACE_ITEMS}
              fullWidth={false}
            />
          </Flex>
          {viewMode === 'graph' && (
            <Flex flexDirection="column" alignItems="flex-end" gap={2}>
              <Flex alignItems="center">
                <Box mr={2}>
                  <Popover
                    minimal={false}
                    position={PopoverPosition.BOTTOM}
                    content={
                      <Box p={2}>
                        <Field name="groupBy" onChange={this.onGroupByChange} small>
                          <Select />
                        </Field>
                        {maxPathCount > 1 && (
                          <Box mx={1} mb={1}>
                            <MultiSlider min={0} max={maxPathCount} disabled={maxPathCount <= 1}>
                              <Handle
                                intentBefore="primary"
                                intentAfter="none"
                                value={collapseFromStart}
                                onChange={(val) => form.getField('collapseFromStart').onChange(val)}
                              />
                              <Handle
                                intentAfter="primary"
                                value={maxPathCount - collapseFromEnd}
                                onChange={(val) => form.getField('collapseFromEnd').onChange(maxPathCount - val)}
                              />
                            </MultiSlider>
                          </Box>
                        )}
                      </Box>
                    }
                  >
                    <Flex alignItems="center">
                      <Text as="div" mr="4px">
                        Group hops by
                      </Text>
                      <Button rightIcon="caret-down">{groupBtnText}</Button>
                    </Flex>
                  </Popover>
                </Box>
                <Flex alignItems="center">
                  <Text as="div" mr="4px">
                    Highlight
                  </Text>
                  <Box mr={1}>
                    <Popover
                      minimal={false}
                      position={PopoverPosition.BOTTOM}
                      content={
                        <Box p={1}>
                          <Flex alignItems="center" m={1}>
                            <Text as="div" whiteSpace="nowrap" mr={1} small>
                              When latency is at least
                            </Text>
                            <Field name="deltaLatencyThreshold" mb={0}>
                              <InputGroup type="number" min="0" rightElement={<Tag>ms</Tag>} width={100} />
                            </Field>
                          </Flex>
                        </Box>
                      }
                    >
                      <Button rightIcon="caret-down">Latency &gt;= {deltaLatencyThreshold}ms</Button>
                    </Popover>
                  </Box>
                  <Box>
                    <Popover
                      minimal={false}
                      position={PopoverPosition.BOTTOM}
                      content={
                        <Box p={1}>
                          <Flex justifyContent="space-between" alignItems="center">
                            <Box whiteSpace="nowrap" mr={1}>
                              When success rate is at or below
                            </Box>
                            <Field name="percSuccessThreshold" mb={0}>
                              <InputGroup type="number" min="0" max="100" rightElement={<Tag>%</Tag>} width={120} />
                            </Field>
                          </Flex>
                        </Box>
                      }
                    >
                      <Button rightIcon="caret-down">
                        {/* round the output to prevent precision goofiness such as 0.55 * 100 = 55.00000000000001  */}
                        Success Rate &lt;= {Math.round(percSuccessThreshold * 100)}%
                      </Button>
                    </Popover>
                  </Box>
                </Flex>
              </Flex>
              <Flex alignItems="center">
                <Field name="collapseTimeouts" showLabel={false} mb={0} mr={2}>
                  <Switch showFieldLabel />
                </Field>
                <Flex alignItems="center">
                  <Field name="moreThanExpected" showLabel={false} mb={0}>
                    <Switch showFieldLabel />
                  </Field>
                  <Tooltip content="Turn this on if you want us to compare latency measured with latency calculated using geo-ip distance">
                    <Icon iconSize={14} icon={FiInfo} color="primary" ml="4px" />
                  </Tooltip>
                </Flex>
                <Button onClick={this.resetForm} ml={2}>
                  Reset
                </Button>
              </Flex>
            </Flex>
          )}
        </Flex>
        {!hasResults && (
          <Card p={1} py={3}>
            <EmptyState icon="disable" title="No results" />
          </Card>
        )}
        {hasResults &&
          !!selectedAgents.length &&
          (viewMode === 'table' ? (
            <TracerouteTable
              traceroutes={traceroutes}
              selectedAgents={selectedAgents}
              selectedIps={this.isHostnameTargetedTest ? [] : selectedIps}
              isPreview={isPreview}
              $exports={$exports}
            />
          ) : (
            <Traceroute
              traceroutes={traceroutes}
              selectedAgents={selectedAgents}
              selectedIps={selectedIps}
              groupBy={groupBy}
              useLocalIp={useLocalIp}
              collapseFromStart={collapseFromStart}
              collapseFromEnd={collapseFromEnd}
              openPaths={openPaths}
              onOpenPaths={this.onOpenPaths}
              closeOpenPaths={this.closeOpenPaths}
              setPaths={this.setPaths}
              deltaLatencyThreshold={deltaLatencyThreshold}
              percSuccessThreshold={percSuccessThreshold}
              onNodeSelect={onNodeSelect}
              selectedNode={selectedNode}
              collapseTimeouts={collapseTimeouts}
              moreThanExpected={moreThanExpected}
              isPreview={isPreview}
            />
          ))}
      </Box>
    );
  }
}

export default TestPath;
